import { useState } from 'react';
import { MdCheckCircle, MdArrowForward } from 'react-icons/md';
import { useMutation } from '@tanstack/react-query';
import { submitLead } from '../../services/leadsService';
import Modal from './Modal';

// ── Validation helpers (Nepal-first, not Nepal-only) ───────────────────────────
const EMAIL_RE = /\S+@\S+\.\S+/;
const emailIsValid = (e) => EMAIL_RE.test(e.trim());

// Nepal mobile: 10 digits starting 97/98 — or international: + and 7–15 digits
const phoneIsValid = (p) => {
  const v = p.trim();
  return /^(97|98)\d{8}$/.test(v) || /^\+\d{7,15}$/.test(v);
};

// Keep only digits, with at most one leading "+"; cap length to stop garbage
const filterPhone = (raw) => {
  const hasPlus = raw.trimStart().startsWith('+');
  const digits  = raw.replace(/\D/g, '').slice(0, 15);
  return hasPlus ? '+' + digits : digits;
};

function Field({ label, children, error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

export default function RequestSetupModal({ isOpen, onClose, defaultSource = 'demo', demoSlug = '', demoRef = '' }) {
  const [form, setForm] = useState({ name: '', clubName: '', phone: '', email: '', city: '', message: '', website: '' });
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const setPhone = (e) => setForm(f => ({ ...f, phone: filterPhone(e.target.value) }));

  const { mutate, isPending } = useMutation({
    mutationFn: (data) => submitLead(data).then(r => r.data),
    onSuccess: () => setDone(true),
    onError:   () => setErrors({ submit: 'Something went wrong. Please try again.' }),
  });

  const handleClose = () => {
    setForm({ name: '', clubName: '', phone: '', email: '', city: '', message: '', website: '' });
    setErrors({});
    setDone(false);
    onClose();
  };

  const validate = () => {
    const e = {};
    const phone = form.phone.trim();
    const email = form.email.trim();
    if (!form.name.trim()) e.name = 'Your name is required.';
    if (!phone && !email) {
      e.phone = 'Please enter a phone number or email.';
    } else {
      if (phone && !phoneIsValid(phone)) e.phone = 'Enter a valid phone number (e.g. 98XXXXXXXX or +countrycode…).';
      if (email && !emailIsValid(email)) e.email = 'Enter a valid email address.';
    }
    return e;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    mutate({
      name:     form.name.trim(),
      clubName: form.clubName.trim(),
      phone:    form.phone.trim(),
      email:    form.email.trim(),
      city:     form.city.trim(),
      message:  form.message.trim(),
      source:   defaultSource,
      demoSlug: demoSlug || '',
      demoRef:  demoRef  || '',
      website:  form.website, // honeypot
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Request Setup" maxWidth="max-w-md">
      {done ? (
        <div className="flex flex-col items-center text-center py-6">
          <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mb-4">
            <MdCheckCircle className="text-green-500 text-4xl" />
          </div>
          <h3 className="font-bold text-gray-800 text-lg mb-2">Request received!</h3>
          <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
            Thanks! We'll be in touch to help set up your club on TrainAxis.
          </p>
          <button
            onClick={handleClose}
            className="mt-6 px-5 py-2.5 bg-primary text-white font-semibold text-sm rounded-xl hover:bg-primary-dark transition-colors"
          >
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Honeypot — hidden from real users */}
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={set('website')}
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}
          />

          <Field label="Your name *" error={errors.name}>
            <input
              type="text"
              value={form.name}
              onChange={set('name')}
              placeholder="Aarav Sharma"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60"
            />
          </Field>

          <Field label="Club name">
            <input
              type="text"
              value={form.clubName}
              onChange={set('clubName')}
              placeholder="Kathmandu Taekwondo Club"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60"
            />
          </Field>

          <Field label="Phone number" error={errors.phone}>
            <input
              type="tel"
              inputMode="tel"
              maxLength={16}
              value={form.phone}
              onChange={setPhone}
              placeholder="98XXXXXXXX"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60"
            />
            <p className="text-xs text-gray-400 mt-1">Phone or email — at least one is required</p>
          </Field>

          <Field label="Email address" error={errors.email}>
            <input
              type="email"
              value={form.email}
              onChange={set('email')}
              placeholder="you@example.com"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60"
            />
          </Field>

          <Field label="City">
            <input
              type="text"
              value={form.city}
              onChange={set('city')}
              placeholder="Kathmandu, Pokhara, …"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60"
            />
          </Field>

          <Field label="Message (optional)">
            <textarea
              value={form.message}
              onChange={set('message')}
              placeholder="Tell us about your club — size, sport, any questions"
              rows={3}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60 resize-none"
            />
          </Field>

          {errors.submit && (
            <p className="text-xs text-red-500 text-center">{errors.submit}</p>
          )}

          <button
            type="submit"
            disabled={isPending}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white font-bold text-sm rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-60"
          >
            {isPending ? 'Sending…' : <><span>Request Setup</span><MdArrowForward /></>}
          </button>

          <p className="text-center text-xs text-gray-400">
            We'll contact you within 24 hours. No spam.
          </p>
        </form>
      )}
    </Modal>
  );
}
